import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { message } from 'antd';
import { Select, Switch } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { UploadCard, GenerateBar } from '..';
import { DEFAULT_COUNT_OPTIONS } from '../GenerateBar';
import styles from './index.module.scss';

const EQUALIZER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas/equalizer.svg';

const ORIENTATION_OPTIONS = [
    { value: 'natural', label: '整体更自然' },
    { value: 'front', label: '正面' },
    { value: 'left', label: '左侧' },
    { value: 'right', label: '右侧' },
];

const EXPRESSION_OPTIONS = [
    { value: 'natural', label: '整体更自然' },
    { value: 'happy', label: '开心' },
    { value: 'serious', label: '严肃' },
    { value: 'surprised', label: '惊讶' },
];

const DEFAULT_REDRAW_MODE_OPTIONS = [
    { value: 'standard', label: '标准' },
];

const UPLOAD_PARAM_KEY_MAP = {
    source: 'srcImage',
    reference: 'refImage',
    match: 'matchImage',
};

const buildInitialUploads = (uploadFields) => Object.fromEntries(
    uploadFields.map((field) => [field.key, null]),
);

const buildInitialSelectValues = (fields) => Object.fromEntries(
    fields.map((field) => [field.key, field.defaultValue ?? field.options?.[0]?.value ?? '']),
);

const buildInitialSwitchValues = (fields) => Object.fromEntries(
    fields.map((field) => [field.key, field.defaultValue ?? field.checkedValue ?? false]),
);

const buildInitialExtraValues = (fields, defaults, inputParams = {}, panelValues = {}) => Object.fromEntries(
    fields.map((field) => {
        const fallbackValue = defaults[field.key];
        const inputValue = inputParams?.[field.key];
        const panelValue = panelValues?.[field.key];

        return [field.key, panelValue ?? inputValue ?? fallbackValue];
    }),
);

const isSameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const DEFAULT_GENERATE_COUNT = 4;

const isValidCount = (count, countOptions) => countOptions.some((option) => option.value === count);

const FunctionPanel = ({
    panelKey,
    uploadFields,
    onClose,
    advancedOptions = {},
    nodeId,
    nodeData,
    onChange,
    disabled = false,
    generateCountOptions = DEFAULT_COUNT_OPTIONS,
    defaultGenerateCount = DEFAULT_GENERATE_COUNT,
    panelWidth = null,
}) => {
    const panelState = nodeData?.panelState || {};
    const inputParams = nodeData?.inputParams || {};
    const normalizedDefaultGenerateCount = isValidCount(defaultGenerateCount, generateCountOptions)
        ? defaultGenerateCount
        : (generateCountOptions[0]?.value ?? DEFAULT_GENERATE_COUNT);
    const [uploads, setUploads] = useState(() => ({
        ...buildInitialUploads(uploadFields),
        ...(panelState.uploads || {}),
    }));
    const [advancedOpen, setAdvancedOpen] = useState(!!panelState.advancedOpen);
    const [orientation, setOrientation] = useState(panelState.orientation || inputParams.orientation || 'natural');
    const [expression, setExpression] = useState(panelState.expression || inputParams.expression || 'natural');
    const [keepHeadwear, setKeepHeadwear] = useState(panelState.keepHeadwear ?? !!inputParams.keepHeadwear);
    const [skipSelect, setSkipSelect] = useState(panelState.skipSelect ?? !!inputParams.skipSelect);
    const {
        showAdvancedSection = true,
        showOrientation = true,
        showExpression = true,
        showKeepHeadwear = true,
        showSkipSelect = true,
        showRedrawMode = false,
        includeOrientationParam = true,
        includeExpressionParam = true,
        includeKeepHeadwearParam = true,
        includeSkipSelectParam = true,
        redrawModeParamKey = 'redrawMode',
        uploadParamKeyMap = UPLOAD_PARAM_KEY_MAP,
        orientationParamKey = 'orientation',
        expressionParamKey = 'expression',
        keepHeadwearParamKey = 'keepHeadwear',
        skipSelectLabel = '跳过选图',
        skipSelectHint = '整组执行时,跳过选图,图片直传下一步',
        orientationLabel = '模特朝向',
        expressionLabel = '表情参考',
        keepHeadwearLabel = '保留头饰',
        orientationOptions = ORIENTATION_OPTIONS,
        expressionOptions = EXPRESSION_OPTIONS,
        redrawModeLabel = '重绘模式',
        redrawModeOptions = DEFAULT_REDRAW_MODE_OPTIONS,
        defaultRedrawMode = redrawModeOptions[0]?.value ?? 'standard',
        extraSelectFields = [],
        extraSwitchFields = [],
    } = advancedOptions;
    const [redrawMode, setRedrawMode] = useState(panelState.redrawMode ?? inputParams[redrawModeParamKey] ?? defaultRedrawMode);
    const [extraSelectValues, setExtraSelectValues] = useState(() => buildInitialExtraValues(
        extraSelectFields,
        buildInitialSelectValues(extraSelectFields),
        inputParams,
        panelState.extraSelectValues,
    ));
    const [extraSwitchValues, setExtraSwitchValues] = useState(() => buildInitialExtraValues(
        extraSwitchFields,
        buildInitialSwitchValues(extraSwitchFields),
        inputParams,
        panelState.extraSwitchValues,
    ));
    const initialGenerateCount = panelState.num ?? nodeData?.inputParams?.num ?? normalizedDefaultGenerateCount;
    const [generateCount, setGenerateCount] = useState(
        isValidCount(initialGenerateCount, generateCountOptions)
            ? initialGenerateCount
            : normalizedDefaultGenerateCount,
    );
    const [draggingSlotKey, setDraggingSlotKey] = useState('');
    const [dragOverSlotKey, setDragOverSlotKey] = useState('');
    const lastSubmittedRef = useRef('');
    const skipNextLinkedRemovalSubmitRef = useRef(false);
    const skipNextExternalSyncSubmitRef = useRef(false);
    const lastExternalSyncSignatureRef = useRef('');
    const linkedSlotSources = useMemo(() => (
        nodeData?.panelState?.linkedSlotSources
        && typeof nodeData.panelState.linkedSlotSources === 'object'
        && !Array.isArray(nodeData.panelState.linkedSlotSources)
            ? nodeData.panelState.linkedSlotSources
            : {}
    ), [nodeData?.panelState?.linkedSlotSources]);

    useEffect(() => {
        const nextUploads = {
            ...buildInitialUploads(uploadFields),
            ...(panelState.uploads || {}),
        };
        const nextAdvancedOpen = !!panelState.advancedOpen;
        const nextOrientation = panelState.orientation || nodeData?.inputParams?.orientation || 'natural';
        const nextExpression = panelState.expression || nodeData?.inputParams?.expression || 'natural';
        const nextKeepHeadwear = panelState.keepHeadwear ?? !!nodeData?.inputParams?.keepHeadwear;
        const nextSkipSelect = panelState.skipSelect ?? !!nodeData?.inputParams?.skipSelect;
        const nextRedrawMode = panelState.redrawMode ?? nodeData?.inputParams?.[redrawModeParamKey] ?? defaultRedrawMode;
        const nextExtraSelectValues = buildInitialExtraValues(
            extraSelectFields,
            buildInitialSelectValues(extraSelectFields),
            nodeData?.inputParams,
            panelState.extraSelectValues,
        );
        const nextExtraSwitchValues = buildInitialExtraValues(
            extraSwitchFields,
            buildInitialSwitchValues(extraSwitchFields),
            nodeData?.inputParams,
            panelState.extraSwitchValues,
        );
        const nextGenerateCount = panelState.num ?? nodeData?.inputParams?.num ?? normalizedDefaultGenerateCount;
        const externalSyncSignature = JSON.stringify({
            uploads: nextUploads,
            advancedOpen: nextAdvancedOpen,
            orientation: nextOrientation,
            expression: nextExpression,
            keepHeadwear: nextKeepHeadwear,
            skipSelect: nextSkipSelect,
            redrawMode: nextRedrawMode,
            extraSelectValues: nextExtraSelectValues,
            extraSwitchValues: nextExtraSwitchValues,
            generateCount: nextGenerateCount,
        });

        if (lastExternalSyncSignatureRef.current === externalSyncSignature) {
            return;
        }
        lastExternalSyncSignatureRef.current = externalSyncSignature;

        const shouldSyncFromNode = (
            !isSameValue(uploads, nextUploads)
            || advancedOpen !== nextAdvancedOpen
            || orientation !== nextOrientation
            || expression !== nextExpression
            || keepHeadwear !== nextKeepHeadwear
            || skipSelect !== nextSkipSelect
            || redrawMode !== nextRedrawMode
            || !isSameValue(extraSelectValues, nextExtraSelectValues)
            || !isSameValue(extraSwitchValues, nextExtraSwitchValues)
            || (
                generateCount !== nextGenerateCount
                && isValidCount(nextGenerateCount, generateCountOptions)
            )
        );

        if (shouldSyncFromNode) {
            skipNextExternalSyncSubmitRef.current = true;
        }

        setUploads((prev) => (isSameValue(prev, nextUploads) ? prev : nextUploads));
        setAdvancedOpen((prev) => (prev === nextAdvancedOpen ? prev : nextAdvancedOpen));
        setOrientation((prev) => (prev === nextOrientation ? prev : nextOrientation));
        setExpression((prev) => (prev === nextExpression ? prev : nextExpression));
        setKeepHeadwear((prev) => (prev === nextKeepHeadwear ? prev : nextKeepHeadwear));
        setSkipSelect((prev) => (prev === nextSkipSelect ? prev : nextSkipSelect));
        setRedrawMode((prev) => (prev === nextRedrawMode ? prev : nextRedrawMode));
        setExtraSelectValues((prev) => (isSameValue(prev, nextExtraSelectValues) ? prev : nextExtraSelectValues));
        setExtraSwitchValues((prev) => (isSameValue(prev, nextExtraSwitchValues) ? prev : nextExtraSwitchValues));
        setGenerateCount((prev) => (
            prev === nextGenerateCount || !isValidCount(nextGenerateCount, generateCountOptions)
                ? prev
                : nextGenerateCount
        ));
    }, [
        defaultRedrawMode,
        extraSelectFields,
        extraSwitchFields,
        nodeData?.inputParams?.num,
        panelState.advancedOpen,
        panelState.expression,
        panelState.extraSelectValues,
        panelState.extraSwitchValues,
        panelState.keepHeadwear,
        panelState.skipSelect,
        panelState.num,
        panelState.orientation,
        panelState.redrawMode,
        panelState.uploads,
        generateCountOptions,
        nodeData?.inputParams,
        normalizedDefaultGenerateCount,
        redrawModeParamKey,
        uploadFields,
    ]);

    const fieldContext = useMemo(() => ({
        advancedOpen,
        uploads,
        orientation,
        expression,
        keepHeadwear,
        skipSelect,
        redrawMode,
        extraSelectValues,
        extraSwitchValues,
        generateCount,
    }), [
        advancedOpen,
        uploads,
        orientation,
        expression,
        keepHeadwear,
        skipSelect,
        redrawMode,
        extraSelectValues,
        extraSwitchValues,
        generateCount,
    ]);

    useEffect(() => {
        const nextSelectValues = {};
        let shouldUpdateSelectValues = false;

        extraSelectFields.forEach((field) => {
            if (!field.resetWhenDisabled || !field.disabled?.(fieldContext)) return;
            const resetValue = field.resetValue ?? field.defaultValue ?? field.options?.[0]?.value ?? '';
            if (extraSelectValues[field.key] === resetValue) return;
            nextSelectValues[field.key] = resetValue;
            shouldUpdateSelectValues = true;
        });

        if (shouldUpdateSelectValues) {
            setExtraSelectValues((prev) => ({
                ...prev,
                ...nextSelectValues,
            }));
        }

        const nextSwitchValues = {};
        let shouldUpdateSwitchValues = false;

        extraSwitchFields.forEach((field) => {
            if (!field.resetWhenDisabled || !field.disabled?.(fieldContext)) return;
            const resetValue = field.resetValue ?? field.uncheckedValue ?? false;
            if (extraSwitchValues[field.key] === resetValue) return;
            nextSwitchValues[field.key] = resetValue;
            shouldUpdateSwitchValues = true;
        });

        if (shouldUpdateSwitchValues) {
            setExtraSwitchValues((prev) => ({
                ...prev,
                ...nextSwitchValues,
            }));
        }
    }, [extraSelectFields, extraSelectValues, extraSwitchFields, extraSwitchValues, fieldContext]);

    useEffect(() => {
        const visibleUploadKeySet = new Set(uploadFields.map((field) => field.key));
        const hiddenLinkedSlotKeys = Object.keys(linkedSlotSources).filter((slotKey) => (
            !visibleUploadKeySet.has(slotKey)
            && !!linkedSlotSources[slotKey]?.edgeId
        ));
        const hiddenUploadKeys = Object.keys(uploads).filter((slotKey) => (
            !visibleUploadKeySet.has(slotKey)
            && !!uploads[slotKey]
        ));

        if (hiddenLinkedSlotKeys.length === 0 && hiddenUploadKeys.length === 0) return;

        skipNextLinkedRemovalSubmitRef.current = true;
        setUploads((prev) => {
            const nextUploads = { ...prev };
            [...new Set([...hiddenLinkedSlotKeys, ...hiddenUploadKeys])].forEach((slotKey) => {
                nextUploads[slotKey] = null;
            });
            return nextUploads;
        });

        hiddenLinkedSlotKeys.forEach((slotKey) => {
            nodeData?.onRemoveLinkedInput?.(nodeId, slotKey, linkedSlotSources[slotKey] || null);
        });
    }, [linkedSlotSources, nodeData, nodeId, uploadFields, uploads]);

    const handleUploadChange = useCallback((key, nextValue) => {
        setUploads((prev) => ({
            ...prev,
            [key]: nextValue,
        }));
    }, []);

    const handleRemoveLinkedUpload = useCallback((key, linkedState) => {
        skipNextLinkedRemovalSubmitRef.current = true;
        setUploads((prev) => ({
            ...prev,
            [key]: null,
        }));
        nodeData?.onRemoveLinkedInput?.(nodeId, key, linkedState || null);
    }, [nodeData, nodeId]);

    const isUploadSlotOccupied = useCallback((slotKey) => (
        !!uploads?.[slotKey]
        || !!linkedSlotSources?.[slotKey]?.edgeId
    ), [linkedSlotSources, uploads]);

    const handleUploadDragStart = useCallback((slotKey) => {
        if (!isUploadSlotOccupied(slotKey)) return;
        setDraggingSlotKey(slotKey);
        setDragOverSlotKey('');
    }, [isUploadSlotOccupied]);

    const handleUploadDragEnd = useCallback(() => {
        setDraggingSlotKey('');
        setDragOverSlotKey('');
    }, []);

    const handleUploadDragOver = useCallback((slotKey, event) => {
        if (!draggingSlotKey || draggingSlotKey === slotKey) return;
        if (!isUploadSlotOccupied(draggingSlotKey)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        setDragOverSlotKey(slotKey);
    }, [draggingSlotKey, isUploadSlotOccupied]);

    const handleUploadDragLeave = useCallback((slotKey) => {
        setDragOverSlotKey((prev) => (prev === slotKey ? '' : prev));
    }, []);

    const handleUploadDrop = useCallback((slotKey, event) => {
        if (!draggingSlotKey || draggingSlotKey === slotKey) return;
        if (!isUploadSlotOccupied(draggingSlotKey)) return;
        event.preventDefault();
        event.stopPropagation();
        nodeData?.onSwapInputSlots?.(nodeId, draggingSlotKey, slotKey);
        setDraggingSlotKey('');
        setDragOverSlotKey('');
    }, [draggingSlotKey, isUploadSlotOccupied, nodeData, nodeId]);

    useEffect(() => {
        if (draggingSlotKey && !isUploadSlotOccupied(draggingSlotKey)) {
            setDraggingSlotKey('');
        }
    }, [dragOverSlotKey, draggingSlotKey, isUploadSlotOccupied]);

    const handleGenerate = useCallback(({ count }) => {
        const missingUploads = uploadFields
            .filter((field) => field.required && !uploads[field.key])
            .map((field) => field.label)
            .filter(Boolean);

        if (missingUploads.length > 0) {
            message.warning(`请先上传${missingUploads.join('、')}`);
            return;
        }

        nodeData?.onExecute?.(nodeId, { count });
        nodeData?.onGenerateComplete?.(nodeId);
    }, [nodeData, nodeId, uploadFields, uploads]);

    const handleExtraSelectChange = useCallback((key, value) => {
        setExtraSelectValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const handleExtraSwitchChange = useCallback((key, value) => {
        const field = extraSwitchFields.find((item) => item.key === key);
        const nextValue = value
            ? (field?.checkedValue ?? true)
            : (field?.uncheckedValue ?? false);

        setExtraSwitchValues((prev) => ({
            ...prev,
            [key]: nextValue,
        }));
    }, [extraSwitchFields]);

    useEffect(() => {
        if (!nodeId || typeof onChange !== 'function') return;
        if (skipNextLinkedRemovalSubmitRef.current) {
            skipNextLinkedRemovalSubmitRef.current = false;
            return;
        }
        if (skipNextExternalSyncSubmitRef.current) {
            skipNextExternalSyncSubmitRef.current = false;
            return;
        }

        const currentLinkedSlotSources = (
            nodeData?.panelState?.linkedSlotSources
            && typeof nodeData.panelState.linkedSlotSources === 'object'
            && !Array.isArray(nodeData.panelState.linkedSlotSources)
        )
            ? nodeData.panelState.linkedSlotSources
            : {};
        const nextLinkedSlotSources = Object.fromEntries(
            Object.entries(currentLinkedSlotSources).filter(([slotKey, linkedState]) => (
                (
                    !!linkedState?.edgeId
                    && uploads?.[slotKey] !== null
                )
                || (
                    uploads?.[slotKey]
                    && uploads[slotKey] === (linkedState?.url || '')
                )
            )),
        );
        const uploadInputParams = Object.fromEntries(
            uploadFields.map((field) => [
                uploadParamKeyMap[field.key] || field.key,
                uploads[field.key] || '',
            ]),
        );
        const nextPatch = {
            inputParams: {
                ...uploadInputParams,
                num: generateCount,
                ...(includeOrientationParam ? { [orientationParamKey]: orientation } : {}),
                ...(includeExpressionParam ? { [expressionParamKey]: expression } : {}),
                ...(includeKeepHeadwearParam ? { [keepHeadwearParamKey]: (keepHeadwear ? 1 : 0) } : {}),
                ...(includeSkipSelectParam ? { skipSelect: (skipSelect ? 1 : 0) } : {}),
                ...(showRedrawMode ? { [redrawModeParamKey]: redrawMode } : {}),
                ...extraSelectValues,
                ...extraSwitchValues,
            },
            panelState: {
                uploads,
                linkedSlotSources: nextLinkedSlotSources,
                advancedOpen,
                orientation,
                expression,
                keepHeadwear,
                skipSelect,
                redrawMode,
                extraSelectValues,
                extraSwitchValues,
                num: generateCount,
            },
        };
        const nextSignature = JSON.stringify(nextPatch);

        if (
            lastSubmittedRef.current === nextSignature
            || (
                isSameValue(nodeData?.inputParams || {}, nextPatch.inputParams)
                && isSameValue(nodeData?.panelState || {}, nextPatch.panelState)
            )
        ) {
            lastSubmittedRef.current = nextSignature;
            return;
        }

        lastSubmittedRef.current = nextSignature;
        onChange(nodeId, nextPatch);
    }, [
        advancedOpen,
        expression,
        extraSelectValues,
        extraSwitchValues,
        generateCount,
        includeExpressionParam,
        includeKeepHeadwearParam,
        includeOrientationParam,
        includeSkipSelectParam,
        keepHeadwear,
        nodeId,
        nodeData?.inputParams,
        nodeData?.panelState,
        onChange,
        orientation,
        orientationParamKey,
        redrawModeParamKey,
        redrawMode,
        showRedrawMode,
        skipSelectHint,
        skipSelectLabel,
        skipSelect,
        expressionParamKey,
        keepHeadwearParamKey,
        uploadParamKeyMap,
        uploadFields,
        uploads,
        handleRemoveLinkedUpload,
    ]);

    const uploadRowClassName = `${styles.uploadRow} ${uploadFields.length === 2 ? styles.uploadRowSingleColumn : ''}`;

    return (
        <div
            className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}
            style={panelWidth ? { width: panelWidth } : undefined}
        >
            <button
                type="button"
                className={styles.closeButton}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => onClose?.(event)}
                aria-label="关闭参数面板"
            >
                <CloseOutlined />
            </button>
            <div className={uploadRowClassName}>
                {uploadFields.map((field) => (
                    <UploadCard
                        key={field.key}
                        label={field.label}
                        required={field.required}
                        value={uploads[field.key]}
                        linkedLabel={linkedSlotSources[field.key]?.imageSequenceLabel || ''}
                        linkedEdgeId={linkedSlotSources[field.key]?.edgeId || ''}
                        onRemoveLinked={() => handleRemoveLinkedUpload(field.key, linkedSlotSources[field.key] || null)}
                        onChange={(nextValue) => handleUploadChange(field.key, nextValue)}
                        draggable={!disabled && isUploadSlotOccupied(field.key)}
                        dragging={draggingSlotKey === field.key}
                        dragOver={dragOverSlotKey === field.key}
                        onDragStart={() => handleUploadDragStart(field.key)}
                        onDragEnd={handleUploadDragEnd}
                        onDragOver={(event) => handleUploadDragOver(field.key, event)}
                        onDragLeave={() => handleUploadDragLeave(field.key)}
                        onDrop={(event) => handleUploadDrop(field.key, event)}
                    />
                ))}
            </div>

            <div className={styles.divider} />

            <GenerateBar
                onGenerate={handleGenerate}
                loading={String(
                    nodeData?.latestExecution?.status
                    || nodeData?.executionStatus
                    || 'idle',
                ).toLowerCase() === 'processing'}
                disabled={!uploadFields
                    .filter((field) => field.required)
                    .every((field) => !!uploads[field.key] || !!linkedSlotSources[field.key]?.edgeId)
                    || disabled
                }
                count={generateCount}
                onCountChange={setGenerateCount}
                countOptions={generateCountOptions}
                defaultCount={normalizedDefaultGenerateCount}
                buttonWidth={120}
                trailingContent={showAdvancedSection ? (
                    <button
                        type="button"
                        className={`${styles.advancedIconButton} ${advancedOpen ? styles.advancedIconButtonActive : ''}`}
                        onClick={() => setAdvancedOpen((prev) => !prev)}
                    >
                        <div className={`${styles.advancedIconWrap} ${advancedOpen ? styles.advancedIconWrapActive : ''}`}>
                            <img
                                src={EQUALIZER_ICON}
                                alt=""
                                width={16}
                                height={16}
                                className={styles.advancedIcon}
                            />
                        </div>
                    </button>
                ) : null}
            />
            {showAdvancedSection ? (
                <div className={`${styles.advancedPanelWrap} ${advancedOpen ? styles.advancedPanelWrapOpen : ''}`}>
                    <div className={styles.advancedPanel}>
                        {showOrientation ? (
                            <div className={styles.advRow}>
                                <span className={styles.advRowLabel}>模特朝向</span>
                                <Select
                                    className={styles.advSelect}
                                    value={orientation}
                                    onChange={setOrientation}
                                    options={orientationOptions}
                                    variant="borderless"
                                    size="small"
                                    popupMatchSelectWidth={false}
                                />
                            </div>
                        ) : null}
                        {showExpression ? (
                            <div className={styles.advRow}>
                                <span className={styles.advRowLabel}>表情参数</span>
                                <Select
                                    className={styles.advSelect}
                                    value={expression}
                                    onChange={setExpression}
                                    options={expressionOptions}
                                    variant="borderless"
                                    size="small"
                                    popupMatchSelectWidth={false}
                                />
                            </div>
                        ) : null}
                        {showKeepHeadwear ? (
                            <div className={styles.advRow}>
                                <span className={styles.advRowLabel}>保留源素材头饰</span>
                                <Switch
                                    checked={keepHeadwear}
                                    onChange={setKeepHeadwear}
                                    size="small"
                                />
                            </div>
                        ) : null}
                        {showRedrawMode ? (
                            <div className={styles.advRow}>
                                <span className={styles.advRowLabel}>{redrawModeLabel}</span>
                                <Select
                                    className={styles.advSelect}
                                    value={redrawMode}
                                    onChange={setRedrawMode}
                                    options={redrawModeOptions}
                                    variant="borderless"
                                    size="small"
                                    popupMatchSelectWidth={false}
                                />
                            </div>
                        ) : null}
                        {extraSelectFields.filter((field) => !field.hidden?.(fieldContext)).map((field) => (
                            <div key={field.key} className={styles.advRow}>
                                <span className={styles.advRowLabel}>{field.label}</span>
                                <Select
                                    className={styles.advSelect}
                                    value={extraSelectValues[field.key]}
                                    onChange={(value) => handleExtraSelectChange(field.key, value)}
                                    options={field.options}
                                    disabled={field.disabled?.(fieldContext)}
                                    variant="borderless"
                                    size="small"
                                    popupMatchSelectWidth={false}
                                />
                            </div>
                        ))}
                        {extraSwitchFields.filter((field) => !field.hidden?.(fieldContext)).map((field) => (
                            <div key={field.key} className={styles.advRow}>
                                <span className={styles.advRowLabel}>{field.label}</span>
                                <Switch
                                    checked={extraSwitchValues[field.key] === (field.checkedValue ?? true)}
                                    disabled={field.disabled?.(fieldContext)}
                                    onChange={(value) => handleExtraSwitchChange(field.key, value)}
                                    size="small"
                                />
                            </div>
                        ))}
                        {showSkipSelect ? (
                            <div className={`${styles.advRow} ${styles.advRowSkipSelect}`}>
                                <div className={styles.advRowText}>
                                    <span className={styles.advRowLabel}>{skipSelectLabel}</span>
                                    <span className={styles.advRowHint}>{skipSelectHint}</span>
                                </div>
                                <Switch
                                    checked={skipSelect}
                                    onChange={setSkipSelect}
                                    size="small"
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default FunctionPanel;
